import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'

import classNames from 'classnames'
import { isToday } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { cloneDeep } from 'lodash-es'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import {
  getAppointmentDateTimeObject,
  isExpeditedBookingAppointment,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import RefreshButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/refreshButton/RefreshButton'
import Dashlet from '@lyrahealth-inc/ui-core/lib/molecules/layouts/dashlet/Dashlet'
import { ExpeditedBookingIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ExpeditedBookingIcon'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './upcomingSessionsDashlet.module.scss'
import { appointmentStatuses } from '../../../common/constants/appConstants'
import { CLIENT_HOME } from '../../../common/constants/routingConstants'
import { getClientFullName } from '../../../common/utils/utils'
import { useAppDispatch } from '../../../data/store'
import { useGetAppointmentsQuery } from '../../clients/clientDetails/data/appointmentsApi'
import { setSelectedClientId } from '../../clients/clientDetails/data/ltClientSlice'
import { isProviderAppointment } from '../dashboardUtils'
import { useDashboardClientsData } from '../hooks/useDashboardClientsData'

interface ClientV2WithNextAppointment extends ClientListClientObject {
  next_appointment?: Appointment
}

const UpcomingSessionsDashlet: React.FC<UpcomingSessionsDashletProps> = ({ providerId }) => {
  const { data: appointments = [], refetch: refetchAppointments } = useGetAppointmentsQuery({ providerId: providerId })
  const navigate = useNavigate()
  const intl = useIntl()
  const { isPreferredNameEnabled } = useFlags()
  const dispatch = useAppDispatch()

  const {
    clients,
    includeInteractedDBTTeensClients,
    isLoading: isLoadingClients,
    refetch: refetchClients,
  } = useDashboardClientsData()
  const [isRefreshingSessions, setIsRefreshingSessions] = useState(false)

  const clientsWithUpcomingAppointments: ClientV2WithNextAppointment[] = []

  const appointmentsToday = appointments.filter((appt: Appointment) => {
    return (
      (!includeInteractedDBTTeensClients || isProviderAppointment(appt, providerId)) &&
      isToday(getDateFromAppointment(appt)) &&
      ![appointmentStatuses.completed, appointmentStatuses.missed, appointmentStatuses.canceled].includes(
        appt.appointmentStatus,
      )
    )
  })

  const addAppointmentForClient = (appointment: Appointment) => {
    const client = clients.find(({ id }) => appointment.userId === id)
    if (client) {
      clientsWithUpcomingAppointments.push({
        ...cloneDeep(client),
        next_appointment: appointment,
      })
    }
  }

  if (includeInteractedDBTTeensClients) {
    // DBT Teens clients may have separate teen and caregiver sessions on the same day.
    appointmentsToday.forEach(addAppointmentForClient)
  } else {
    // Preserve the legacy dashboard behavior for non-DBT Teens providers: one row per client.
    clients.forEach((client) => {
      const nextAppointmentToday = appointmentsToday.find((appointment) => appointment.userId === client.id)
      if (nextAppointmentToday) {
        addAppointmentForClient(nextAppointmentToday)
      }
    })
  }
  clientsWithUpcomingAppointments.sort((a, b) => {
    return (
      getAppointmentDateTimeObject(a.next_appointment!).valueOf() -
      getAppointmentDateTimeObject(b.next_appointment!).valueOf()
    )
  })

  const renderClientListItem = (client: ClientV2WithNextAppointment) => {
    const appointmentTime = zonedTimeToUtc(
      `${client?.next_appointment?.startDate} ${client?.next_appointment?.startTime}`,
      client?.next_appointment?.timeZone ?? 'America/Los_Angeles',
    )

    return (
      <div className={styles['dashlet-item-container']} key={client.next_appointment?.appointmentId}>
        <button
          type='button'
          className={styles['dashlet-item-content']}
          key={client.id}
          onClick={() => {
            dispatch(setSelectedClientId(client.id))
            navigate(CLIENT_HOME.route)
          }}
          data-test-id={`UpcomingSessionDashlet-list-item-${`${client.first_name} ${client.last_name}`}`}
        >
          <div className={styles.left}>
            <div
              className={classNames(styles.time, {
                [styles.crossed]: new Date() > appointmentTime,
              })}
            >
              {intl.formatDate(appointmentTime, {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
            <div className={styles.name}>{getClientFullName(client, isPreferredNameEnabled)}</div>
          </div>
          <div className={styles.right}>
            {client.next_appointment && isExpeditedBookingAppointment(client.next_appointment) && (
              <div className={styles['icon-wrapper']}>
                <ExpeditedBookingIcon />
              </div>
            )}
            <div className={styles['session-count']}>{`Session ${client?.next_appointment?.sessionNumber}`}</div>
          </div>
        </button>
      </div>
    )
  }

  return (
    <div className={styles['dashlet-container']} data-test-id='UpcomingSessionDashlet-content'>
      <Dashlet
        title={`Today's upcoming sessions${isLoadingClients ? '' : ` (${clientsWithUpcomingAppointments.length})`}`}
        buttons={
          <RefreshButton
            size={20}
            isLoading={isRefreshingSessions}
            onClick={() => {
              setIsRefreshingSessions(true)
              Promise.all([refetchClients(), refetchAppointments().unwrap()])
                .catch(() => undefined)
                .finally(() => {
                  setIsRefreshingSessions(false)
                })
            }}
            data-testid={tID('UpcomingSessionDashlet-refreshButton')}
          />
        }
        initiallyExpanded={true}
        isLoading={isLoadingClients}
        maxMinimizedChildren={isLoadingClients ? 3 : Math.max(clientsWithUpcomingAppointments.length, 1)}
      >
        {clientsWithUpcomingAppointments.length === 0 ? (
          <div className={styles['empty-dashlet']}>No upcoming sessions today</div>
        ) : (
          clientsWithUpcomingAppointments.map(renderClientListItem)
        )}
      </Dashlet>
    </div>
  )
}

type UpcomingSessionsDashletProps = {
  providerId: string
}

export default UpcomingSessionsDashlet
